/**
 * RicaricaModal — F-009.3.
 *
 * Dialog di ricarica: tagli suggeriti (€50/100/250/500) + importo custom, con
 * validazione del minimo (€10, da `MIN_RICARICA_CENTS` shared). Alla conferma
 * invoca `onConfirm(amountCents)` — il chiamante avvia la Checkout Session e
 * reindirizza a Stripe. Presentazionale: lo stato `isPending` arriva da fuori
 * (mutation), così il componente è testabile senza rete.
 */
import { useState } from 'react';
import { MIN_RICARICA_CENTS } from '@app/shared';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { centsToEuro, RICARICA_PRESETS_CENTS } from '../lib/format';

const MIN_CENTS = Number(MIN_RICARICA_CENTS);

interface RicaricaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (amountCents: number) => void;
  isPending?: boolean;
}

export function RicaricaModal({ open, onOpenChange, onConfirm, isPending }: RicaricaModalProps) {
  const [euro, setEuro] = useState('');

  const parsed = Number(euro.replace(',', '.'));
  const amountCents = Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN;
  const isValid = Number.isFinite(amountCents) && amountCents >= MIN_CENTS;
  const showError = euro.trim() !== '' && !isValid;

  function handleConfirm() {
    if (!isValid || isPending) return;
    onConfirm(amountCents);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ricarica wallet</DialogTitle>
          <DialogDescription>
            Scegli un importo o inseriscine uno personalizzato. Minimo{' '}
            {centsToEuro(MIN_CENTS)}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {RICARICA_PRESETS_CENTS.map((cents) => {
              const selected = amountCents === cents;
              return (
                <Button
                  key={cents}
                  type="button"
                  variant={selected ? 'default' : 'outline'}
                  onClick={() => setEuro(String(cents / 100))}
                  aria-pressed={selected}
                >
                  {centsToEuro(cents)}
                </Button>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ricarica-euro">Altro importo (€)</Label>
            <Input
              id="ricarica-euro"
              type="number"
              inputMode="decimal"
              min={MIN_CENTS / 100}
              step="1"
              value={euro}
              onChange={(e) => setEuro(e.target.value)}
              placeholder="es. 75"
              aria-invalid={showError}
              aria-describedby={showError ? 'ricarica-euro-error' : undefined}
            />
            {showError ? (
              <p id="ricarica-euro-error" className="text-sm text-destructive" role="alert">
                L'importo minimo è {centsToEuro(MIN_CENTS)}.
              </p>
            ) : null}
          </div>

          <p className="text-xs text-muted-foreground">
            Verrai reindirizzato a Stripe per il pagamento sicuro (carta, 3D Secure). Il
            saldo sarà aggiornato entro pochi secondi dalla conferma.
          </p>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isPending}>
              Annulla
            </Button>
          </DialogClose>
          <Button type="button" onClick={handleConfirm} disabled={!isValid || isPending}>
            {isPending ? 'Reindirizzamento…' : 'Paga con Stripe'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
